import {
  type ChatMessageDraft,
  type ConversationItem,
  OFFLINE_ACTIONS,
  shortDate,
  timeOf,
  todayIso,
  useAuth,
  useChatActions,
  useChatContacts,
  useConversationPeople,
  useConversations,
  useCurrentContext,
  useMessages,
  useQueuedActions,
} from '@shkola/api';
import { isWithinQuietHours } from '@shkola/core';
import { MoonIcon } from '@phosphor-icons/react/Moon';
import { type FormEvent, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Spinner } from '../components/ui';
import { ReportDialog } from '../care/ReportDialog';
import {
  Block,
  DARK,
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import {
  CaretLeftIcon,
  ClockIcon,
  FlagIcon,
  PaperPlaneTiltIcon,
  PlusIcon,
  UsersThreeIcon,
} from '../design/icons';
import { dayLong } from '../student/home/model';

/*
 * «Повідомлення» in «Форми 2» (28.10): the title on the paper, two white blocks side by side — the
 * conversations on the left (each with its face: the initials on a soft gradient, a class chat with its
 * people), the open conversation on the right with the days between the messages, mine on the dark, the
 * others' on the paper; the field to write at the bottom with the dark pill to send. On a phone — the
 * list, then the conversation with a way back.
 */

const when = (iso: string) =>
  iso.slice(0, 10) === todayIso() ? timeOf(iso) : shortDate(iso.slice(0, 10));

const FIELD =
  'rounded-[14px] bg-white outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

/** The soft colours a face can take: the same name always the same. */
const FACE_TONES: ReadonlyArray<Grad> = [
  'lavender',
  'sky',
  'mint',
  'apricot',
  'pink',
  'butter',
  'aqua',
  'cobalt',
];
const toneOf = (name: string): Grad => {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return FACE_TONES[hash % FACE_TONES.length] as Grad;
};

const initialsOf = (name: string) =>
  name
    .split(/[\s-]+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '•';

/** A face of a conversation: the initials on a soft gradient; a class chat — its people. */
function Face({
  name,
  group = false,
  size = 44,
}: {
  name: string;
  group?: boolean;
  size?: number;
}) {
  const tone = group ? 'lavender' : toneOf(name);
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.34),
        backgroundImage: grad(tone),
        color: GRAD[tone].ink,
      }}
    >
      {group ? <UsersThreeIcon size={Math.round(size * 0.46)} weight="fill" /> : initialsOf(name)}
    </span>
  );
}

/** A count of the new: a small soft rectangle in the orange, as the counts of the menu. */
function Count({ n }: { n: number }) {
  return (
    <span
      className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-[7px] px-1.5 text-[11.5px] leading-none font-medium"
      style={{ backgroundImage: grad('orange'), color: '#FFFFFF' }}
    >
      {n}
    </span>
  );
}

function NewChat({
  schoolId,
  onOpened,
  onClose,
}: {
  schoolId: string;
  onOpened: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const contacts = useChatContacts(schoolId);
  const open = useChatActions().openDirect;
  const [query, setQuery] = useState('');
  const list = (contacts.data ?? []).filter((c) =>
    `${c.last_name} ${c.first_name}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <Modal title={t('chats.new')} onClose={onClose}>
      <input
        aria-label={t('chats.search')}
        className={`mb-3 h-11 w-full px-4 text-[15px] ${FIELD}`}
        style={{ color: INK }}
        placeholder={t('chats.search')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {contacts.isPending ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="py-8 text-center text-[15px]" style={{ color: INK_SOFT }}>
          {t('chats.noContacts')}
        </p>
      ) : (
        <ul className="-mx-2 flex max-h-96 flex-col overflow-y-auto">
          {list.slice(0, 100).map((c) => {
            const name = `${c.last_name} ${c.first_name}`;
            return (
              <li key={c.member_id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-[16px] px-2 py-2 text-left transition-colors hover:bg-[#F5F3EF] disabled:opacity-50"
                  disabled={open.isPending}
                  onClick={() =>
                    void open.mutateAsync({ schoolId, withMemberId: c.member_id }).then((id) => {
                      onOpened(id);
                      onClose();
                    })
                  }
                >
                  <Face name={name} size={36} />
                  <span className="min-w-0 flex-1 truncate text-[15px]" style={{ color: INK }}>
                    {name}
                  </span>
                  <span className="shrink-0 text-[12.5px]" style={{ color: INK_SOFT }}>
                    {c.role_hint ?? t(`roles.${c.kind}`)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

/** The days between the messages: «Сьогодні», «Субота, 17 жовтня». */
function DayMark({ iso }: { iso: string }) {
  const { t } = useTranslation();
  return (
    <p className="my-2 flex justify-center">
      <span
        className="rounded-[8px] px-2.5 py-1 text-[12px] font-medium"
        style={{ backgroundColor: PAPER, color: INK_SOFT }}
      >
        {iso === todayIso() ? t('events.today') : dayLong(iso)}
      </span>
    </p>
  );
}

function Thread({
  conversation,
  schoolId,
  canReport,
  onBack,
}: {
  conversation: ConversationItem;
  schoolId: string;
  /** students can complain about a message to the psychologist and the director */
  canReport: boolean;
  /** on a phone: back to the list */
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [reporting, setReporting] = useState<{ id: string; body: string }>();
  const messages = useMessages(conversation.id, conversation.myMemberId);
  const people = useConversationPeople(conversation.id);
  const actions = useChatActions();
  const [body, setBody] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  // written without connection: shown at once, sent in order when the connection is back
  const queued = useQueuedActions<ChatMessageDraft>(OFFLINE_ACTIONS.sendMessage).filter(
    (m) => m.conversationId === conversation.id,
  );
  const count = (messages.data?.length ?? 0) + queued.length;
  const group = conversation.kind !== 'direct';

  // reading the conversation marks it read; new messages scroll into view
  const { mutate: markRead } = actions.markRead;
  useEffect(() => {
    if (conversation.unread > 0)
      markRead({ conversationId: conversation.id, memberId: conversation.myMemberId });
  }, [conversation.id, conversation.unread, conversation.myMemberId, markRead]);
  useEffect(() => {
    // a block body: newer browsers return a promise from scrollIntoView, which React would
    // take for a cleanup function
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [count]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    actions.send.mutate({
      schoolId,
      conversationId: conversation.id,
      authorId: conversation.myMemberId,
      body: text,
      clientId: crypto.randomUUID(),
    });
    setBody('');
  };

  const list = messages.data ?? [];
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 pb-3">
        <button
          type="button"
          aria-label={t('chats.title')}
          title={t('chats.title')}
          className={`${ringButton} md:hidden`}
          onClick={onBack}
        >
          <CaretLeftIcon size={15} weight="bold" aria-hidden />
        </button>
        <Face name={conversation.title} group={group} size={40} />
        <h2
          className="min-w-0 truncate font-display text-[24px] leading-[30px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {conversation.title}
        </h2>
      </header>
      <div className="-mx-2 min-h-0 flex-1 overflow-y-auto rounded-[20px] px-2 py-1">
        {messages.isPending ? (
          <Spinner />
        ) : messages.isError ? (
          <p className="py-8 text-center text-[15px]" style={{ color: INK_SOFT }}>
            {t('common.error')}
          </p>
        ) : (
          list.map((m, i) => {
            const day = m.createdAt.slice(0, 10);
            const newDay = i === 0 || list[i - 1]?.createdAt.slice(0, 10) !== day;
            const sameAuthor =
              !newDay && list[i - 1]?.authorId === m.authorId && list[i - 1]?.mine === m.mine;
            return (
              <Fragment key={m.id}>
                {newDay && <DayMark iso={day} />}
                <div
                  className={`group flex items-center gap-2 ${m.mine ? 'justify-end' : 'justify-start'} ${sameAuthor ? 'mt-1' : 'mt-3'}`}
                >
                  <div
                    className={`max-w-[78%] rounded-[20px] px-3.5 py-2 text-[15px] leading-[21px] ${m.mine ? 'rounded-br-[6px] text-white' : 'rounded-bl-[6px]'}`}
                    style={
                      m.mine ? { backgroundColor: DARK } : { backgroundColor: PAPER, color: INK }
                    }
                  >
                    {!m.mine && group && !sameAuthor && (
                      <p className="mb-0.5 text-[12px] font-medium" style={{ color: INK_SOFT }}>
                        {people.data?.[m.authorId]}
                      </p>
                    )}
                    <p className="whitespace-pre-line">{m.body}</p>
                    <p
                      className="mt-0.5 text-right text-[11px]"
                      style={{ color: m.mine ? 'rgb(255 255 255 / 0.6)' : INK_FAINT }}
                    >
                      {timeOf(m.createdAt)}
                    </p>
                  </div>
                  {canReport && !m.mine && (
                    <button
                      type="button"
                      onClick={() => setReporting({ id: m.id, body: m.body })}
                      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-white px-3 text-[12.5px] font-medium opacity-0 ring-1 ring-[#E4E0D7] transition ring-inset group-hover:opacity-100 hover:bg-[#FAF9F6] focus:opacity-100 [@media(hover:none)]:opacity-100"
                      style={{ color: INK_SOFT }}
                    >
                      <FlagIcon size={13} weight="fill" aria-hidden />
                      {t('care.report.action')}
                    </button>
                  )}
                </div>
              </Fragment>
            );
          })
        )}
        {queued.map((m) => (
          <div key={m.clientId} className="mt-3 flex justify-end">
            <div
              className="max-w-[78%] rounded-[20px] rounded-br-[6px] px-3.5 py-2 text-[15px] leading-[21px] text-white opacity-60"
              style={{ backgroundColor: DARK }}
            >
              <p className="whitespace-pre-line">{m.body}</p>
              <p className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-white/80">
                <ClockIcon size={12} weight="fill" aria-hidden />
                {t('offline.queuedMessage')}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      {isWithinQuietHours(new Date(), { from: '21:00', to: '08:00' }) && (
        <p
          className="mt-3 flex items-start gap-2 rounded-[16px] px-3.5 py-2.5 text-[13px] leading-[18px]"
          style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
        >
          <MoonIcon size={15} weight="fill" aria-hidden className="mt-px shrink-0" />
          {t('chats.quietHours')}
        </p>
      )}
      <form className="mt-3 flex items-end gap-2" onSubmit={submit}>
        <textarea
          aria-label={t('chats.placeholder')}
          className={`max-h-40 min-h-11 flex-1 resize-none rounded-[22px] px-4 py-[11px] text-[15px] leading-[21px] [field-sizing:content] ${FIELD}`}
          style={{ color: INK }}
          rows={1}
          maxLength={4000}
          placeholder={t('chats.placeholder')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) submit(e);
          }}
        />
        <button
          type="submit"
          aria-label={t('chats.send')}
          className={`${pillClass('dark')} pl-1.5 disabled:opacity-35 sm:pl-4.5`}
          disabled={!body.trim()}
        >
          <span className="hidden sm:inline">{t('chats.send')}</span>
          <PillIcon icon={PaperPlaneTiltIcon} />
        </button>
      </form>
      {reporting && (
        <ReportDialog
          schoolId={schoolId}
          messageId={reporting.id}
          quote={reporting.body}
          onClose={() => setReporting(undefined)}
        />
      )}
    </div>
  );
}

/** Nothing open yet, or nothing at all: the two talking, quiet. */
function Quiet({ text }: { text: string }) {
  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 text-center">
      <EventArt kind="meeting" tone="lavender" size={104} />
      <p className="max-w-[280px] text-[15px]" style={{ color: INK_SOFT }}>
        {text}
      </p>
    </div>
  );
}

export function MessagesPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const { me } = useAuth();
  const memberIds = (me?.memberships ?? [])
    .filter((m) => m.school_id === ctx.schoolId)
    .map((m) => m.member_id);
  const conversations = useConversations(memberIds);
  const actions = useChatActions();
  const [selected, setSelected] = useState<string>();
  const [creating, setCreating] = useState(false);
  const current = conversations.data?.find((c) => c.id === selected);
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const all = conversations.data ?? [];
  const unread = all.reduce((n, c) => n + c.unread, 0);

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('chats.title')}
        note={unread > 0 ? t('notifications.unread', { count: unread }) : undefined}
      >
        {homeroom && (
          <button
            type="button"
            className={`${pillClass('light')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
            onClick={() =>
              void actions.openClass
                .mutateAsync({ classId: homeroom.id, kind: 'class_parents' })
                .then(setSelected)
            }
          >
            <span className="max-lg:sr-only">
              {t('chats.classParents', { class: homeroom.label })}
            </span>
            <PillIcon icon={UsersThreeIcon} />
          </button>
        )}
        <button
          type="button"
          className={`${pillClass('dark')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
          onClick={() => setCreating(true)}
        >
          <span className="max-lg:sr-only">{t('chats.new')}</span>
          <PillIcon icon={PlusIcon} />
        </button>
      </PageHead>
      <div className="grid min-h-0 grid-cols-1 gap-4 md:h-[calc(100dvh-210px)] md:min-h-[540px] md:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
        <Block className={`min-h-0 !p-2 md:!p-3 ${current ? 'hidden md:flex' : ''}`}>
          {conversations.isPending ? (
            <Spinner />
          ) : conversations.isError ? (
            <p className="p-4 text-[15px]" style={{ color: INK_SOFT }}>
              {t('common.error')}
            </p>
          ) : all.length === 0 ? (
            <Quiet text={t('chats.empty')} />
          ) : (
            <ul className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
              {all.map((c) => {
                const on = c.id === selected;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      aria-current={on || undefined}
                      onClick={() => setSelected(c.id)}
                      className={`flex w-full items-center gap-3 rounded-[18px] px-2.5 py-2.5 text-left transition-colors ${on ? '' : 'hover:bg-[#F5F3EF]'}`}
                      style={on ? { backgroundColor: '#EFECE5' } : undefined}
                    >
                      <Face name={c.title} group={c.kind !== 'direct'} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2">
                          <span
                            className={`min-w-0 flex-1 truncate text-[15px] leading-5 ${c.unread ? 'font-medium' : ''}`}
                            style={{ color: INK }}
                          >
                            {c.title}
                          </span>
                          {c.lastMessage && (
                            <span className="shrink-0 text-[12px]" style={{ color: INK_FAINT }}>
                              {when(c.lastMessage.at)}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 flex items-center gap-2">
                          <span
                            className="min-w-0 flex-1 truncate text-[13px] leading-[18px]"
                            style={{ color: c.unread ? INK : INK_SOFT }}
                          >
                            {c.lastMessage
                              ? `${c.lastMessage.mine ? `${t('chats.you')}: ` : ''}${c.lastMessage.body}`
                              : ''}
                          </span>
                          {c.unread > 0 && <Count n={c.unread} />}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Block>
        <Block
          className={`min-h-0 ${current ? 'h-[calc(100dvh-190px)] min-h-[440px] md:h-auto md:min-h-0' : 'hidden md:flex'}`}
        >
          {current ? (
            <Thread
              key={current.id}
              conversation={current}
              schoolId={ctx.schoolId}
              canReport={ctx.kind === 'student'}
              onBack={() => setSelected(undefined)}
            />
          ) : (
            <Quiet text={all.length === 0 ? t('chats.empty') : t('chats.pick')} />
          )}
        </Block>
      </div>
      {creating && (
        <NewChat
          schoolId={ctx.schoolId}
          onOpened={setSelected}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
